/** Email domain used to identify internal RISA team members. */
export const RISA_EMAIL_DOMAIN = "@risalabs.ai";

/**
 * Returns true when the email belongs to the internal RISA team.
 *
 * @param email - Logged-in user email from auth state.
 * @return boolean: True when the email ends with `@risalabs.ai`.
 */
export function isRisaTeamEmail(email: string | null | undefined): boolean {
  const normalizedEmail = email?.trim().toLowerCase() ?? "";
  return normalizedEmail.endsWith(RISA_EMAIL_DOMAIN);
}

/**
 * Resolves the email used to label a support ticket comment author.
 *
 * @param authorEmail - Author email from the support tickets API.
 * @param authorName - Author name fallback when it contains an email address.
 * @returns Trimmed email when available, otherwise an empty string.
 */
function resolveSupportTicketAuthorEmail(
  authorEmail: string | null | undefined,
  authorName: string | null | undefined,
): string {
  const trimmedEmail = authorEmail?.trim() ?? "";
  if (trimmedEmail) {
    return trimmedEmail;
  }

  const trimmedName = authorName?.trim() ?? "";
  return trimmedName.includes("@") ? trimmedName : "";
}

/**
 * Maps a comment author email to the sender label shown in the thread.
 *
 * @param authorEmail - Author email from the support tickets API.
 * @param authorName - Author name fallback when email is stored in author_name.
 * @returns "Risa" for @risalabs.ai addresses, "Client" for all other emails,
 *   or the author name / "Unknown" when no email is available.
 */
export function mapSupportTicketCommentAuthorLabel(
  authorEmail: string | null | undefined,
  authorName?: string | null | undefined,
): string {
  const email = resolveSupportTicketAuthorEmail(authorEmail, authorName);
  if (!email) {
    return authorName?.trim() || "Unknown";
  }

  return isRisaTeamEmail(email) ? "Risa" : "Astera";
}
